import getNextFocusTodoId from '../selectors/getNextFocusTodoId';
import getSelectedFolderView from '../selectors/getSelectedFolderView';
import getSelectedFolderViewState from '../selectors/getSelectedFolderViewState';
import moveDragAndDroppableItem from 'owa-dnd/lib/utils/moveDragAndDroppableItem';
import TodoFolderView from '../store/schema/TodoFolderView';
import { Todo, TodoFolder } from 'owa-todo-types';
import { findInArray } from '../utils/findInArray';
import { mutator } from 'satcheljs';
import { processLoadedFolders } from '../actions/todoFolderStoreActions';
import {
    addRecentlyCompletedTodosToFolderView,
    addTodosToFolderView,
    insertTodosAtIndex,
    removeTodoFromView,
    updatePositions,
    moveFromCompletedToActiveList,
    moveFromActiveToCompletedList,
} from '../actions/todoFolderViewActions';

export const addTodosToFolderViewMutator = mutator(addTodosToFolderView, actionMessage => {
    const { itemsAdded, deleteAll, addToFront } = actionMessage;
    const todoFolderView = getSelectedFolderView();
    if (deleteAll) {
        todoFolderView.activeTaskIds = [];
    }

    addToTasksIdsList(itemsAdded, addToFront, todoFolderView.activeTaskIds);
});

export const addRecentlyCompletedTodosToFolderViewMutator = mutator(
    addRecentlyCompletedTodosToFolderView,
    actionMessage => {
        const { itemsAdded, deleteAll, addToFront } = actionMessage;
        const todoFolderView = getSelectedFolderView();
        if (deleteAll) {
            todoFolderView.recentlyCompletedTaskIds = [];
        }

        addToTasksIdsList(itemsAdded, addToFront, todoFolderView.recentlyCompletedTaskIds);
    }
);

function addToTasksIdsList(itemsAdded: Todo[], addToFront: boolean, taskIdsList: string[]) {
    const todoFolderView = getSelectedFolderView();

    if (itemsAdded) {
        const itemIdsAdded = itemsAdded.map(todo => todo.Id);

        if (addToFront) {
            taskIdsList.splice(0, 0, ...itemIdsAdded);
        } else {
            taskIdsList.push(...itemIdsAdded);
        }

        itemsAdded.forEach(todo => {
            todoFolderView.todoItems.set(todo.Id, todo);
        });
    }
}

export const insertTodosAtIndexMutator = mutator(insertTodosAtIndex, actionMessage => {
    const { itemsAdded, atIndex } = actionMessage;

    if (itemsAdded) {
        const itemIdsAdded = itemsAdded.map(todo => todo.Id);
        const folderView = getSelectedFolderView();
        folderView.activeTaskIds.splice(atIndex, 0, ...itemIdsAdded);

        itemsAdded.forEach(todo => {
            folderView.todoItems.set(todo.Id, todo);
        });
    }
});

export const removeTodoFromFolderViewMutator = mutator(removeTodoFromView, actionMessage => {
    const folderView = getSelectedFolderView();
    const itemId = actionMessage.itemId;

    const indexInActiveList = folderView.activeTaskIds.indexOf(itemId);
    const fromActiveList = indexInActiveList > -1;
    const taskIds = fromActiveList ? folderView.activeTaskIds : folderView.recentlyCompletedTaskIds;

    getSelectedFolderViewState().focusTodoId = getNextFocusTodoId(itemId, fromActiveList);
    const index = taskIds.indexOf(itemId);
    if (index > -1) {
        taskIds.splice(index, 1);

        // Intentionally not removing the item from the map. When completing a task,
        // it will move to the recently completed, where we do want to preserve the task item.
        // Yes, this means that todo items will accumulate,
        // but they do get cleared every time we switch folder or reload OWA.
        // Mail list view lives with this, and they cache across all folders, and haven't run into issues yet.
    }
});

export const updatePositionsMutator = mutator(updatePositions, actionMessage => {
    const { fromIndex, toIndex } = actionMessage;
    const folderViewState = getSelectedFolderView();
    moveDragAndDroppableItem(folderViewState.activeTaskIds, fromIndex, toIndex);
});

export const processLoadedFoldersMutator = mutator(processLoadedFolders, actionMessage => {
    // If we still have null as the selected folder id,
    // replace it with the real id of the default folder,
    // This happens when the panel is first opened (tasks are loaded before the folders)
    // so that the correct id is used in requests to update the to list
    const folders: TodoFolder[] = actionMessage.folders;
    if (!folders || folders.length == 0) {
        return;
    }

    const folderView = getSelectedFolderView();
    if (folderView.folderId) {
        return;
    }

    const foundDefaultFolder = findInArray(folders, item => item.IsDefaultFolder);
    if (foundDefaultFolder) {
        folderView.folderId = foundDefaultFolder.item.Id;
    }
});

export const moveFromCompletedToActiveListMutator = mutator(
    moveFromCompletedToActiveList,
    actionMessage => {
        const { itemId, insertIndex } = actionMessage;
        const folderView = getSelectedFolderView();

        const indexInCompleted = folderView.recentlyCompletedTaskIds.indexOf(itemId);

        if (indexInCompleted > -1) {
            getSelectedFolderViewState().focusTodoId = getNextFocusTodoId(itemId, false);
            folderView.recentlyCompletedTaskIds.splice(indexInCompleted, 1);
            folderView.activeTaskIds.splice(insertIndex, 0, ...[itemId]);
        }
    }
);

export const moveFromActiveToCompletedListMutator = mutator(
    moveFromActiveToCompletedList,
    actionMessage => {
        const { itemId } = actionMessage;
        const folderView = getSelectedFolderView();

        const indexInActive = folderView.activeTaskIds.indexOf(itemId);

        if (indexInActive > -1) {
            getSelectedFolderViewState().focusTodoId = getNextFocusTodoId(itemId, true);
            folderView.activeTaskIds.splice(indexInActive, 1);
            folderView.recentlyCompletedTaskIds.splice(0, 0, ...[itemId]);
        }
    }
);

export { Todo, TodoFolderView };
